import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import { InfoOutlined } from "@mui/icons-material";
import {
  fonts,
  headingFonts,
  mainButtonStyles,
  noOutline,
  primaryButtonStyles,
  secondaryButtonStyles,
  textFieldStyles,
} from "../../../Styles/Styles";

export default function GovernmentIDSubmission() {
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [loading, setLoading] = useState(false); // New loading state
  const [openModal, setOpenModal] = useState(false); // Modal state

  const handleImageChange = (e, side) => {
    const file = e.target.files[0];
    if (side === "frontImage") {
      setFrontImage(file);
    } else {
      setBackImage(file);
    }
  };

  const handleSubmit = async () => {
    setLoading(true); // Set loading to true when submit starts
    const formData = new FormData();
    if (frontImage) {
      formData.append("frontImage", frontImage);
    }
    if (backImage) {
      formData.append("backImage", backImage);
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/submit-id`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit ID");
      }

      alert("ID submitted successfully. Our team will review it shortly!");

      // Clear the image fields after successful submission
      setFrontImage(null);
      setBackImage(null);
      setOpenModal(false); // Close the modal on successful submission
    } catch (error) {
      console.error("Error submitting ID:", error);
      alert("Error submitting ID: " + error.message);
    } finally {
      setLoading(false); // Reset loading state after submit
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          "&:focus, &:active": { outline: "none" },
        }}
      >
        <Button
          onClick={() => setOpenModal(true)} // Open modal on button click
          sx={{ ...mainButtonStyles }}
        >
          Become Verified Seller
        </Button>
        <Tooltip
          title="Your uploaded ID is securely encrypted and will only be accessible to authorized personnel. The ID will automatically be deleted after 30 days or immediately upon successful verification."
          arrow
        >
          <IconButton
            size="small"
            sx={{
              color: "var(--primary-color)",
              ...noOutline,
            }}
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Modal for uploading images */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ ...headingFonts }}>Submit Government ID</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, ...fonts }}>
            Please upload the front and back images of your Government-issued
            ID.
          </Typography>
          <TextField
            type="file"
            accept="image/*"
            onChange={(e) => handleImageChange(e, "frontImage")}
            label="Upload Front Side"
            variant="outlined"
            fullWidth
            margin="normal"
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <BadgeOutlined />
                </InputAdornment>
              ),
            }}
            sx={{ ...textFieldStyles }}
          />
          <TextField
            type="file"
            accept="image/*"
            onChange={(e) => handleImageChange(e, "backImage")}
            label="Upload Back Side"
            variant="outlined"
            fullWidth
            margin="normal"
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <BadgeOutlined />
                </InputAdornment>
              ),
            }}
            sx={{ ...textFieldStyles }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="contained"
            sx={{ ...secondaryButtonStyles }}
            onClick={() => setOpenModal(false)}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{ ...primaryButtonStyles }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <span>Submitting...</span>
                <CircularProgress size={20} sx={{ color: "white" }} />
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
